/**
 * Following (subscribing to) a brand, shop or store: the toggle that used to
 * live inside `EntityDetailLayout`, so the promotion page can offer the same
 * action for its brand (master D-4: a follow CTA on upcoming and ended
 * promotions).
 *
 * - A guest goes to /login; a member toggles.
 * - Optimistic: `subscribed` and the follower count flip at once; a failed
 *   toggle flips them back.
 * - Once the server accepted it, the Apollo cache's `isSubscribed` /
 *   `subscriberCounts` for that entity are updated, so every page that reads
 *   the same normalised entity (the brand page and a promotion page of that
 *   brand) shows one state. A failed toggle never touched the cache, so there
 *   is nothing to undo there (undoing it anyway took one follower off the
 *   cached count on every failure).
 * - `follow` is sent to analytics only once the server accepted a follow.
 *
 * The state follows the entity it is given: whenever the object changes
 * (loaded, refetched, or rewritten by the cache update above) it is read
 * again. Pass the query's own object, not a copy made on every render.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  TOGGLE_SUBSCRIBE_BRAND,
  TOGGLE_SUBSCRIBE_SHOP,
  TOGGLE_SUBSCRIBE_STORE,
} from '@/graphql/mutations';
import { useAuth } from '@/context/AuthContext';
import { trackFollow } from '@/lib/analytics';

export type FollowKind = 'brand' | 'shop' | 'store';

/** The GraphQL type of each kind, for the cache id when the object has no `__typename`. */
export const ENTITY_TYPENAMES: Readonly<Record<FollowKind, string>> = {
  brand: 'Brand',
  shop: 'Shop',
  store: 'Store',
};

const SUBSCRIBE_MUTATIONS = {
  brand: TOGGLE_SUBSCRIBE_BRAND,
  shop: TOGGLE_SUBSCRIBE_SHOP,
  store: TOGGLE_SUBSCRIBE_STORE,
} as const;

/** What the toggle reads from the entity. */
export interface FollowTarget {
  id: string;
  isSubscribed?: boolean | null;
  subscriberCounts?: number | null;
}

export interface FollowToggle {
  /** Whether the viewer follows the entity (optimistic). */
  subscribed: boolean;
  /** A toggle is in flight. */
  subscribing: boolean;
  /** The follower count, moved by the viewer's own toggles (optimistic). */
  followers: number;
  /** A guest goes to /login; a member follows or unfollows. No-op without an entity. */
  toggleFollow: () => void;
}

export function useFollowToggle(
  kind: FollowKind,
  entity: FollowTarget | null | undefined,
): FollowToggle {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [toggleSubscribe, { loading: subscribing, client }] = useMutation(SUBSCRIBE_MUTATIONS[kind]);

  useEffect(() => {
    if (!entity) return;
    setSubscribed(!!entity.isSubscribed);
    setFollowers(entity.subscriberCounts ?? 0);
  }, [entity]);

  const toggleFollow = () => {
    if (!entity) return;
    if (!token) {
      navigate('/login');
      return;
    }
    const next = !subscribed;
    const cacheId = client.cache.identify({
      __typename: (entity as { __typename?: string }).__typename ?? ENTITY_TYPENAMES[kind],
      id: entity.id,
    });
    setSubscribed(next);
    setFollowers((f) => Math.max(0, f + (next ? 1 : -1)));
    toggleSubscribe({
      variables: { id: entity.id },
      update: (cache) => {
        cache.modify({
          id: cacheId,
          fields: {
            isSubscribed: () => next,
            subscriberCounts: (count: number = 0) => Math.max(0, count + (next ? 1 : -1)),
          },
        });
      },
    })
      .then(() => {
        if (next) trackFollow({ contentType: kind, itemId: entity.id });
      })
      .catch(() => {
        // `update` runs only for an accepted toggle (errorPolicy 'none'), so
        // the cache still holds the old values: only the local state flips back.
        setSubscribed(!next);
        setFollowers((f) => Math.max(0, f + (next ? -1 : 1)));
      });
  };

  return { subscribed, subscribing, followers, toggleFollow };
}
